import {
  isVisibleWithinScope,
  type BaseEntity,
  type EntityTypeConfig,
} from "@brains/plugins";
import { z } from "@brains/utils/zod";
import type {
  TopicsPluginConfig,
  TopicSourceRolePolicy,
} from "../schemas/config";
import { TOPIC_ENTITY_TYPE } from "./constants";

const sourceMetadataSchema = z.looseObject({
  title: z.string().optional(),
  status: z.unknown().optional(),
});

export function includesTopicSourceType(
  entityType: string,
  config: TopicsPluginConfig,
  entityTypeConfig: EntityTypeConfig,
): boolean {
  return (
    entityType !== TOPIC_ENTITY_TYPE &&
    !config.excludeEntityTypes.includes(entityType) &&
    (config.includeEntityTypes.includes("*") ||
      config.includeEntityTypes.includes(entityType)) &&
    entityTypeConfig.projectionSource !== false
  );
}

/** Whether topics are drawn from this type: included and given weight. */
export function isTopicSourceType(
  entityType: string,
  config: TopicsPluginConfig,
  entityTypeConfig: EntityTypeConfig,
): boolean {
  return (
    includesTopicSourceType(entityType, config, entityTypeConfig) &&
    topicSourcePolicy(entityType, config, entityTypeConfig).weight > 0
  );
}

/** The entity types topics are drawn from, sorted. */
export function topicSourceTypes(
  entityService: {
    getEntityTypes(): string[];
    getEntityTypeConfig(type: string): EntityTypeConfig;
  },
  config: TopicsPluginConfig,
): string[] {
  return entityService
    .getEntityTypes()
    .filter((type) =>
      isTopicSourceType(type, config, entityService.getEntityTypeConfig(type)),
    )
    .sort();
}

export function topicSourcePolicy(
  entityType: string,
  config: TopicsPluginConfig,
  entityTypeConfig: EntityTypeConfig,
): TopicSourceRolePolicy {
  const role =
    config.sourceRoleOverrides[entityType] ??
    entityTypeConfig.projectionSourceRole ??
    (entityTypeConfig.projectionSource === false ? "excluded" : "primary");
  const policy = config.sourceRolePolicies[role];
  return {
    weight:
      Object.keys(config.sourceWeights).length > 0
        ? (config.sourceWeights[entityType] ?? 1)
        : policy.weight,
    canMint:
      config.mintableEntityTypes.length > 0
        ? config.mintableEntityTypes.includes(entityType)
        : policy.canMint,
  };
}

export function isTopicSourceEligible(
  entity: BaseEntity,
  config: TopicsPluginConfig,
): boolean {
  if (!isVisibleWithinScope(entity.visibility, config.extractionVisibility))
    return false;
  const parsed = sourceMetadataSchema.safeParse(entity.metadata);
  if (!parsed.success) return false;
  const status = parsed.data.status;
  return (
    status === undefined ||
    status === null ||
    (typeof status === "string" && config.extractableStatuses.includes(status))
  );
}

export function topicSourceTitle(entity: BaseEntity): string {
  const parsed = sourceMetadataSchema.safeParse(entity.metadata);
  return parsed.success ? (parsed.data.title ?? entity.id) : entity.id;
}
